import React, { useState, useEffect } from "react";
import { Modal, Steps, Row, Col, notification } from "antd";
import CarePlanForm from "./CarePlanForm";

import { create as CreateCarePlan } from "../../api/CarePlan";
import { update as UpdateCarePlan } from "../../api/CarePlan";

import { formatFirstname, formatLastname } from '../../utils';

import { care_types } from "./constants";

import moment from "moment";

const { Step } = Steps;

const LayoutSmallStep = {
  direction: "vertical",
  size: "small",
  progressDot: true,
};

const CarePlanWizard = (props) => {
  const { carePlan, surgicalIntervention } = props;
  const [step, setStep] = useState(0);
  const [underStep, setUnderStep] = useState(0);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);

  const carePlanForm = React.createRef();

  const onClose = () => {
    props.onClose(false);
    setStep(0);
    setUnderStep(0);
    setForm(null);
  };

  const OnChangeStep = async (current) => {
    let values = await carePlanForm.current.getValues();
    await updateForm(values);

    setStep(current);
    setUnderStep(0);
  };

  const onFinish = async (values) => {
    const formUpdated = await updateForm(values);

    if (!formSubmitable(formUpdated)) {
      notification['warning']({
        message: "Impossible d'enregistrer les soins.",
        description: "Veulliez saisir au minimum la date d'affectaction et un soin.",
      });
      return;
    }

    submitCarePLan();
  };

  const formSubmitable = (formValues) => {
    if (!formValues.hasOwnProperty('action_time')) {
      return false;
    }

    const cares = Object.keys(formValues).filter(item => item !== "action_time");

    let caresActives = false;
    cares.forEach(care => {
      if (formValues[care]) {
        caresActives = true;
      }
    });

    return caresActives;
  };

  const nextStep = async (values) => {
    await updateForm(values);

    let lenSteps = care_types.length;
    let lenChildren = care_types[step].children.length;

    if (step === lenSteps - 1 && underStep === lenChildren - 1) {
      return;
    }

    if (underStep < lenChildren - 1) {
      setUnderStep(underStep + 1);
    } else {
      setStep(step + 1);
      setUnderStep(0);
    }
  };

  const previousStep = (values) => {
    updateForm(values);

    let lenChildren = care_types[step].children.length;

    if (step === 0) {
      return;
    }

    if (underStep === 0 && lenChildren > 0) {
      let lenPreviousChildren = care_types[step - 1].children.length;
      setStep(step - 1);
      setUnderStep(lenPreviousChildren - 1);
    } else {
      setUnderStep(underStep - 1);
    }
  };

  const updateForm = (values) => {
    let formUpdate = form !== null ? form : {};
    Object.keys(values).map((key) => {
      formUpdate[key] = values[key];
    });
    setForm(formUpdate);

    return formUpdate;
  };

  const getCarePlanForm = () => {
    let newCarePlan = {
      surgical_intervention_id: surgicalIntervention.id,
      action_time: null,
      cares_performed: [],
      cares_count: {},
    };

    newCarePlan["action_time"] = moment(form["action_time"]).format(
      "YYYY-MM-DDTHH:mm"
    );

    newCarePlan["cares_performed"] = Object.keys(form)
      .filter((key) => form[key] === true)
      .map((key) => parseInt(key));

    Object.keys(form)
      .filter((key) => typeof form[key] === "number")
      .forEach((key) => {
        newCarePlan["cares_count"][key] = form[key];
      });

    return newCarePlan;
  };

  const submitCarePLan = async () => {
    setLoading(true);
    const newCarePlan = getCarePlanForm();

    if (carePlan && carePlan.id) {
      await UpdateCarePlan(carePlan.id, newCarePlan).then(() => {
        setLoading(false);
      }).catch(() => {});
    } else {
      await CreateCarePlan(newCarePlan).then(() => {
        setLoading(false);
      }).catch(() => {});
    }

    props.refresh();
    onClose();
  };

  useEffect(async () => {
    if (carePlan === null) {
      setForm(null);
      return;
    }

    let { cares_performed, cares_count } = carePlan;
    let values = {
      'action_time': moment(carePlan.action_time),
    };
    cares_performed.map((key) => {
      values[key] = true;
    });

    Object.keys(cares_count).map((key) => {
      values[key] = cares_count[key];
    });

    let formUpdate = (form !== null) ? {...form} : {};
    Object.keys(values).map((key) => {
      formUpdate[key] = values[key];
    });

    await updateForm(formUpdate);
  }, [props.carePlan, props.visible]);

  let wizardForm = [];

  care_types.forEach((item, indexStep) => {
    let title = (
      <div>
        {item.label}
        {item.children.length > 0 ? (
          <span className="px-1" style={{ opacity: ".2" }}>
            - ({item.children.length})
          </span>
        ) : null}
      </div>
    );

    wizardForm.push(
      <Step
        key={indexStep}
        title={title}
        icon={item.icon}
        description={
          <Steps
            {...LayoutSmallStep}
            current={underStep}
            style={{
              marginLeft: 5,
              display: step === indexStep ? "block" : "none",
            }}
          >
            {item.children.map((child, indexChildren) => (
              <Step key={indexChildren} title={child.label} />
            ))}
          </Steps>
        }
      />
    );
  });

  let stepActive = care_types.find((item) => parseInt(item.key) === step);
  let underStepActive = null;
  if (!!stepActive.children) {
    underStepActive = stepActive.children.find(
      (item) => parseInt(item.key) === underStep
    );
  }

  let fields = [];
  if (!!stepActive.fields) {
    fields = stepActive.fields;
  } else if (!!underStepActive?.fields) {
    fields = underStepActive.fields;
  }

  let lenSteps = care_types.length;
  let lenChildren = care_types[step].children.length;
  let canNext = !(step === lenSteps - 1 && underStep === lenChildren - 1);

  const { care_unit=null, patient=null } = surgicalIntervention || {};

  const title = ["Soins"];
  if (patient) {
    title.push(
      "de",
      formatFirstname(patient.firstname),
      formatLastname(patient.lastname),
      "-",
      moment(patient.birthdate).format("DD/MM/YYYY"),
    );
  }

  return (
    <>
      <Modal
        title={
          <div>
            {title.join(' ')}
            <br />
            <small style={{ opacity: 0.5 }}>
              Description des soins effectués sur le patient lors de la rotation
            </small>
          </div>
        }
        centered
        visible={props.visible}
        onCancel={() => onClose()}
        width="95vw"
        footer={null}
      >
        <div style={{ overflow: "auto" }}>
          <Row style={{ height: "75vh" }}>
            <Col span={6} style={{ borderRight: "1px solid #f0f0f0" }}>
              <Steps
                current={step}
                onChange={(e) => OnChangeStep(e)}
                direction="vertical"
              >
                {wizardForm}
              </Steps>
            </Col>

            <Col span={18} className="pl-3">
              <div>
                <CarePlanForm
                  ref={carePlanForm}
                  careUnit={care_unit}
                  title={stepActive.label}
                  subTitle={underStepActive ? underStepActive.label : null}
                  fields={fields}
                  carePlan={form}
                  loading={loading}
                  canPrevious={step !== 0}
                  canNext={canNext}
                  previous={previousStep}
                  next={nextStep}
                  submit={onFinish}
                  closeModal={onClose}
                />
              </div>
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  );
};

export default CarePlanWizard;
